import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import api from '../utils/api';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/user/get_dashboard')
      .then(res => {
        if (res.data?.success) setStats(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const planData = () => {
    try {
      return typeof user?.plan === 'string' ? JSON.parse(user.plan) : user?.plan || {};
    } catch { return {}; }
  };

  const plan = planData();
  const dash = stats?.data;
  const st = dash?.stats;
  const tokenLimit = dash?.aiTokens?.limit ?? 0;
  const tokenUsed = dash?.aiTokens?.used ?? 0;
  const tokenRatio = tokenLimit > 0 ? tokenUsed / tokenLimit : 0;
  const showTokenBanner = !loading && tokenLimit > 0 && tokenRatio >= 0.8;

  const greeting = () => {
    const hour = new Date().getHours();
    const name = user?.name?.split(' ')[0] || '';
    if (lang === 'en') {
      if (hour < 12) return `Good morning, ${name}`;
      if (hour < 18) return `Good afternoon, ${name}`;
      return `Good evening, ${name}`;
    } else {
      if (hour < 12) return `Buenos días, ${name}`;
      if (hour < 18) return `Buenas tardes, ${name}`;
      return `Buenas noches, ${name}`;
    }
  };

  const particles = useMemo(
    () =>
      [...Array(15)].map((_, i) => ({
        key: i,
        style: {
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${8 + Math.random() * 12}s`,
          width: `${2 + Math.random() * 3}px`,
          height: `${2 + Math.random() * 3}px`,
          opacity: 0.08 + Math.random() * 0.12,
        },
      })),
    [],
  );

  const cards = [
    {
      label: { en: 'Conversations', es: 'Conversaciones' },
      value: st?.totalConversations ?? '—',
      icon: '💬',
      color: 'var(--accent-green)',
    },
    {
      label: { en: 'Messages Today', es: 'Mensajes Hoy' },
      value: st?.messagesToday ?? '—',
      icon: '📨',
      color: 'var(--accent-cyan)',
    },
    {
      label: { en: 'Active Profiles', es: 'Perfiles Activos' },
      value: st?.activeInstances ?? '—',
      icon: '📱',
      color: 'var(--accent-purple)',
    },
    {
      label: { en: 'AI tokens used', es: 'Tokens IA usados' },
      value: dash?.aiTokens
        ? `${dash.aiTokens.used.toLocaleString()} / ${dash.aiTokens.limit.toLocaleString()}`
        : '—',
      icon: '🧠',
      color: 'var(--accent-green)',
    },
  ];

  return (
    <div className="dashboard">
      <div className="dash-particles" aria-hidden="true">
        {particles.map((p) => (
          <span key={p.key} className="dash-particle" style={p.style} />
        ))}
      </div>
      <div className="dashboard__inner">
      <div className="dashboard__header">
        <h1>{greeting()}</h1>
        <p className="dashboard__subtitle">
          {lang === 'en' ? "Here's what's happening with your WhatsApp today" : 'Esto es lo que pasa con tu WhatsApp hoy'}
        </p>
      </div>

      {loading ? (
        <div className="dashboard__loading">
          <div className="spinner" />
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="dashboard__cards">
            {cards.map((card, i) => (
              <div className="stat-card" key={i}>
                <div className="stat-card__icon" style={{ background: `${card.color}15`, color: card.color }}>
                  {card.icon}
                </div>
                <div className="stat-card__info">
                  <span className="stat-card__value">{card.value}</span>
                  <span className="stat-card__label">{card.label[lang]}</span>
                </div>
              </div>
            ))}
          </div>

          {showTokenBanner && (
            <div
              className={`dashboard__token-banner${tokenRatio >= 1 ? ' dashboard__token-banner--exhausted' : ''}`}
            >
              <span className="dashboard__token-banner-icon">{tokenRatio >= 1 ? '⛔' : '⚠️'}</span>
              <div className="dashboard__token-banner-text">
                <strong>
                  {tokenRatio >= 1
                    ? lang === 'en'
                      ? 'AI tokens exhausted'
                      : 'Tokens IA agotados'
                    : lang === 'en'
                      ? 'AI tokens running low'
                      : 'Tokens IA bajos'}
                </strong>
                <p>
                  {tokenRatio >= 1
                    ? lang === 'en'
                      ? 'Your assistant will not reply until you upgrade or add tokens. Open Plans to fix this.'
                      : 'Tu asistente no responderá hasta que mejores el plan o añadas tokens. Abre Planes.'
                    : lang === 'en'
                      ? 'You have used most of your monthly AI tokens. Upgrade or buy token packs to avoid interruptions.'
                      : 'Has usado la mayor parte de tus tokens IA del mes. Mejora el plan o compra paquetes para evitar cortes.'}
                </p>
              </div>
              <Link to="/app/tokens" className="dashboard__token-banner-link">
                {lang === 'en' ? 'Manage' : 'Gestionar'}
              </Link>
            </div>
          )}

          {/* Quick Actions */}
          <div className="dashboard__section">
            <h2 className="dashboard__section-title">
              {lang === 'en' ? 'Quick Actions' : 'Acciones Rápidas'}
            </h2>
            <div className="dashboard__actions">
              <ActionCard
                icon="💬"
                title={lang === 'en' ? 'Connect WhatsApp' : 'Conectar WhatsApp'}
                desc={lang === 'en' ? 'Scan QR to link your number' : 'Escanea QR para vincular tu número'}
                to="/app/whatsapp"
              />
              <ActionCard
                icon="🤖"
                title={lang === 'en' ? 'Configure AI' : 'Configurar IA'}
                desc={lang === 'en' ? 'Set up your AI assistant' : 'Configura tu asistente IA'}
                to="/app/ai-config"
              />
            </div>
          </div>

          {/* Plan Info */}
          <div className="dashboard__section">
            <h2 className="dashboard__section-title">
              {lang === 'en' ? 'Your Plan' : 'Tu Plan'}
            </h2>
            <div className="plan-card">
              <div className="plan-card__header">
                <span className="plan-card__name">{plan?.title || plan?.name || 'Free'}</span>
                {plan?.price != null && plan?.price !== '' && (
                  <span className="plan-card__price">${plan.price}/{lang === 'en' ? 'mo' : 'mes'}</span>
                )}
              </div>
              <div className="plan-card__bars">
                <PlanBar
                  label={lang === 'en' ? 'Contacts' : 'Contactos'}
                  used={user?.contact || 0}
                  max={Number(plan?.contact_limit) || 100}
                />
                <PlanBar
                  label={lang === 'en' ? 'WA Profiles' : 'Perfiles WA'}
                  used={st?.activeInstances || 0}
                  max={Number(plan?.qr_account) || 1}
                />
                {dash?.aiTokens && (
                  <PlanBar
                    label={lang === 'en' ? 'AI tokens (period)' : 'Tokens IA (periodo)'}
                    used={dash.aiTokens.used}
                    max={dash.aiTokens.limit}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

function ActionCard({ icon, title, desc, to }) {
  const navigate = useNavigate();
  return (
    <div
      className="action-card"
      onClick={() => navigate(to)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(to)}
      role="button"
      tabIndex={0}
    >
      <span className="action-card__icon">{icon}</span>
      <div>
        <strong>{title}</strong>
        <p>{desc}</p>
      </div>
      <span className="action-card__arrow">→</span>
    </div>
  );
}

function PlanBar({ label, used, max }) {
  const safeMax = max > 0 ? max : 1;
  const pct = Math.min((Number(used) / safeMax) * 100, 100);
  return (
    <div className="plan-bar">
      <div className="plan-bar__info">
        <span>{label}</span>
        <span>{Number(used).toLocaleString()} / {Number(max).toLocaleString()}</span>
      </div>
      <div className="plan-bar__track">
        <div className="plan-bar__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
